<template>
    <div class="form-container">
        <AddressField
            :name="name"
            :label="label"
            :searchName="searchName"
            :searchLabel="searchLabel"
            :addressFields="addressFields"
            :customErrorMessage="errorMessage"
            :validationRules="addressFields.Postcode.validationRules"
        />
    </div>
</template>

<script>
import { useStore } from 'vuex'

export default {
    // static settings to initialize step (not reactive inside this file!)
    stepDefinition() {
        return {
            name: 'previousAddress',
            hideContinue: true,
            stringOnSummary: true,
            selected: null,
            manual: false,
            supportsSearch: true,
            route: {
                relativeUrl: `previous-address`,
                name: 'apply.previousAddress',
                props: { addressType: 'previous', formSection: 'address' },
                meta: {
                    title: 'form.sections.address.previousAddress.heading',
                },
            },
            fields: {
                previousAddressBuildingNumber: {
                    value: null,
                    mappedKey: 'prev_building_number',
                    validationRules: {
                        requiredIfEmpty: 'previousAddressBuildingName',
                        max: 50,
                    },
                },
                previousAddressBuildingName: {
                    value: null,
                    mappedKey: 'prev_building_name',
                    validationRules: {
                        requiredIfEmpty: 'previousAddressBuildingNumber',
                        max: 50,
                    },
                },
                previousAddressStreetName: {
                    value: null,
                    mappedKey: 'prev_street_name',
                    validationRules: {
                        required: true,
                        min: 2,
                        max: 50,
                    },
                },
                previousAddressCity: {
                    value: null,
                    mappedKey: 'prev_city',
                    validationRules: {
                        required: true,
                        max: 50,
                    },
                },
                previousAddressCountry: {
                    value: null,
                    mappedKey: 'prev_country',
                },
                previousAddressCounty: {
                    value: null,
                    mappedKey: 'prev_county',
                    validationRules: {
                        required: true,
                        min: 3,
                        max: 50,
                    },
                },
                previousAddressPostcode: {
                    value: null,
                    mappedKey: 'prev_postcode',
                    validationRules: {
                        required: true,
                        min: 5,
                        max: 8,
                        ukPostcode: true,
                    },
                    upperCase: true,
                },
            },
        }
    },

    setup() {
        const store = useStore()

        return {
            store,
        }
    },

    created() {
        this.emitter.emit('stepLoaded')

        this.addressFields.Postcode.validationValue = this.postcodeValue
        this.addressFields.BuildingName.validationValue = this.buildingNameValue
        this.addressFields.BuildingNumber.validationValue = this.buildingNumberValue
        this.addressFields.StreetName.validationValue = this.streetNameValue
        this.addressFields.City.validationValue = this.cityValue
        this.addressFields.County.validationValue = this.countyValue

        this.addressFields.Postcode.validationRules = this.store.getters.fieldValidationRules(`previousAddressPostcode`)
        this.addressFields.BuildingName.validationRules =
            this.store.getters.fieldValidationRules(`previousAddressBuildingName`)
        this.addressFields.BuildingNumber.validationRules =
            this.store.getters.fieldValidationRules(`previousAddressBuildingNumber`)
        this.addressFields.StreetName.validationRules =
            this.store.getters.fieldValidationRules(`previousAddressStreetName`)
        this.addressFields.City.validationRules = this.store.getters.fieldValidationRules(`previousAddressCity`)
        this.addressFields.County.validationRules = this.store.getters.fieldValidationRules(`previousAddressCounty`)

        this.emitter.all.delete('addressField:selected')
        this.emitter.on('addressField:selected', async () => {
            await this.clearFields()
        })
    },

    data() {
        return {
            name: 'previousAddress',
            label: this.$t('form.sections.address.previousAddress.label'),
            errorMessage: this.$t('form.fields.postcode.errorMessage'),
            tooltip: null,
            searchName: 'previousAddressPostcode',
            searchLabel: this.$t('form.fields.address.labels.postcode'),
            addressFields: {
                BuildingName: {
                    name: 'previousAddressBuildingName',
                    label: this.$t('form.fields.buildingName.label'),
                    icon: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.buildingName.errorMessage'),
                },
                BuildingNumber: {
                    name: 'previousAddressBuildingNumber',
                    label: this.$t('form.fields.buildingNumber.label'),
                    icon: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.buildingNumber.errorMessage'),
                },
                StreetName: {
                    name: 'previousAddressStreetName',
                    label: this.$t('form.fields.streetName.label'),
                    icon: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.streetName.errorMessage'),
                },
                City: {
                    name: 'previousAddressCity',
                    label: this.$t('form.fields.city.label'),
                    icon: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.city.errorMessage'),
                },
                County: {
                    name: 'previousAddressCounty',
                    label: this.$t('form.fields.address.labels.county'),
                    icon: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.county.errorMessage'),
                },
                Postcode: {
                    name: 'previousAddressPostcode',
                    label: this.$t('form.fields.address.labels.postcode'),
                    icon: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.postcode.errorMessage'),
                },
            },
            clearFieldSteps: ['previousAddressDuration'],
        }
    },
    computed: {
        postcodeValue: function () {
            return this.store.getters.fieldValue(`previousAddressPostcode`)
        },
        buildingNameValue: function () {
            return this.store.getters.fieldValue(`previousAddressBuildingName`)
        },
        buildingNumberValue: function () {
            return this.store.getters.fieldValue(`previousAddressBuildingNumber`)
        },
        streetNameValue: function () {
            return this.store.getters.fieldValue(`previousAddressStreetName`)
        },
        cityValue: function () {
            return this.store.getters.fieldValue(`previousAddressCity`)
        },
        countyValue: function () {
            return this.store.getters.fieldValue(`previousAddressCounty`)
        },
    },
    watch: {
        postcodeValue: function (value) {
            this.addressFields.Postcode.validationValue = value
        },
        buildingNameValue: function (value) {
            this.addressFields.BuildingName.validationValue = value
        },
        buildingNumberValue: function (value) {
            this.addressFields.BuildingNumber.validationValue = value
        },
        streetNameValue: function (value) {
            this.addressFields.StreetName.validationValue = value
        },
        cityValue: function (value) {
            this.addressFields.City.validationValue = value
        },
        countyValue: function (value) {
            this.addressFields.County.validationValue = value
        },
    },
    methods: {
        async clearFields() {
            await this.store.dispatch('clearSteps', this.clearFieldSteps)
        },
    },
}
</script>
